import { v4 as uuidv4 } from "uuid";
import { Tables } from "../supabase/database.types";
import supabase from "../supabase/supabase";

type Document = Tables<"Documents">;

export interface LocalDocument {
  localId: string;
  title: string;
  originalText: string | null;
  editedText: string | null;
  createdAt: string;
  updatedAt: string;
  duration: number; // Recording duration in seconds (0 if not from mic)
}

export class DocumentService {
  private static readonly LOCAL_STORAGE_KEY = "writevoice_documents";
  private static readonly DOCUMENT_COUNT_KEY = "writevoice_document_count";
  private static readonly AUTH_PROMPTED_KEY = "writevoice_auth_prompted";

  // Check if user is authenticated
  static async isAuthenticated(): Promise<boolean> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    return !!user;
  }

  // Get all local documents
  static getLocalDocuments(): LocalDocument[] {
    const stored = localStorage.getItem(this.LOCAL_STORAGE_KEY);
    return stored ? JSON.parse(stored) : [];
  }

  // Save document locally
  static saveLocalDocument(
    document: Omit<LocalDocument, "localId" | "createdAt" | "updatedAt">
  ): LocalDocument {
    const documents = this.getLocalDocuments();
    const newDoc: LocalDocument = {
      ...document,
      localId: uuidv4(),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      duration: document.duration || 0, // Default to 0 if not provided
    };

    documents.push(newDoc);
    localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(documents));
    this.incrementDocumentCount();

    return newDoc;
  }

  // Update local document
  static updateLocalDocument(
    localId: string,
    updates: Partial<LocalDocument>
  ): LocalDocument | null {
    const documents = this.getLocalDocuments();
    const index = documents.findIndex((doc) => doc.localId === localId);

    if (index === -1) return null;

    documents[index] = {
      ...documents[index],
      ...updates,
      updatedAt: new Date().toISOString(),
    };

    localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(documents));
    return documents[index];
  }

  // Delete local document
  static deleteLocalDocument(localId: string): boolean {
    const documents = this.getLocalDocuments();
    const filtered = documents.filter((doc) => doc.localId !== localId);

    if (filtered.length === documents.length) return false;

    localStorage.setItem(this.LOCAL_STORAGE_KEY, JSON.stringify(filtered));
    return true;
  }

  // Get document count
  static getDocumentCount(): number {
    const count = localStorage.getItem(this.DOCUMENT_COUNT_KEY);
    return count ? parseInt(count) : 0;
  }

  // Increment document count
  private static incrementDocumentCount(): void {
    const count = this.getDocumentCount();
    localStorage.setItem(this.DOCUMENT_COUNT_KEY, String(count + 1));
  }

  // Check if should prompt for auth (after 3 documents)
  static shouldPromptAuth(): boolean {
    const hasPrompted = localStorage.getItem(this.AUTH_PROMPTED_KEY) === "true";
    const count = this.getDocumentCount();
    return count >= 3 && !hasPrompted;
  }

  // Mark auth prompt as shown
  static markAuthPrompted(): void {
    localStorage.setItem(this.AUTH_PROMPTED_KEY, "true");
  }

  // Sync local documents to cloud
  static async syncToCloud(
    userId: string
  ): Promise<{ success: boolean; synced: number; errors: string[] }> {
    const localDocs = this.getLocalDocuments();
    const errors: string[] = [];
    let synced = 0;
    
    // 1. Sync documents directly to Supabase (parallel processing)
    const syncPromises = localDocs.map(async (doc) => {
      try {
        // Check if document already exists (in case of duplicate sync)
        const { data: existingDoc } = await supabase
          .from("Documents")
          .select("id")
          .eq("user_id", userId)
          .eq("local_id", doc.localId)
          .maybeSingle();

        if (!existingDoc) {
          const { error } = await supabase.from("Documents").insert({
            user_id: userId,
            title: doc.title,
            original_text: doc.originalText,
            edited_text: doc.editedText,
            local_id: doc.localId,
            created_at: doc.createdAt,
            updated_at: doc.updatedAt,
          });

          if (error) {
            return {
              success: false,
              error: `Failed to sync document "${doc.title}": ${error.message}`,
            };
          } else {
            return { success: true };
          }
        } else {
          return { success: true };
        }
      } catch (e) {
        return {
          success: false,
          error: `Unexpected error syncing "${doc.title}"`,
        };
      }
    });

    const results = await Promise.all(syncPromises);
    
    // Process results
    results.forEach((result) => {
      if (result.success) {
        synced++;
      } else if (result.error) {
        errors.push(result.error);
      }
    });
    
    // 2. Sync usage data to backend
    try {
      const wordCountStorage = localStorage.getItem("writevoice_word_count");
      if (!wordCountStorage) {
        // Skip usage sync if no data
      } else {
        const wordCountData = JSON.parse(wordCountStorage);
        const { enhancedWords = 0 } = wordCountData;
        
        // Only sync if there's actual usage
        if (enhancedWords > 0) {
          // Calculate total duration from all local documents
          const totalDuration = localDocs.reduce((sum, doc) => sum + (doc.duration || 0), 0);
          
          const { data: session } = await supabase.auth.getSession();
          if (session?.session?.access_token) {
            const response = await fetch(
              `${process.env.REACT_APP_EXPRESS_BACKEND_URL || "https://api.writevoice.io"}/api/sync`,
              {
                method: "POST",
                headers: {
                  Authorization: `Bearer ${session.session.access_token}`,
                  "Content-Type": "application/json",
                },
                body: JSON.stringify({
                  usage: {
                    words: enhancedWords,
                    duration: totalDuration
                  },
                }),
              }
            );

            if (!response.ok) {
              console.error("Failed to sync usage data:", await response.text());
            }
          }
        }
      }
    } catch (error) {
      console.error("Error syncing usage data:", error);
      // Don't fail the whole sync if usage sync fails
    }

    // 3. Clear local storage after successful sync
    if (synced === localDocs.length) {
      localStorage.removeItem(this.LOCAL_STORAGE_KEY);
      localStorage.removeItem(this.DOCUMENT_COUNT_KEY);
      localStorage.removeItem(this.AUTH_PROMPTED_KEY);
      localStorage.removeItem("writevoice_word_count");
    }

    return {
      success: errors.length === 0,
      synced,
      errors,
    };
  }

  // Get a single document by ID (cloud only, returns null for local docs)
  static async getDocument(id: string): Promise<Document | null> {
    const isAuth = await this.isAuthenticated();

    if (!isAuth) return null;

    const {
      data: { user },
    } = await supabase.auth.getUser();

    if (!user) return null;

    const { data, error } = await supabase
      .from("Documents")
      .select("*")
      .eq("id", id)
      .eq("user_id", user.id)
      .maybeSingle();

    if (error) {
      console.error("Error fetching document:", error);
      return null;
    }

    return data;
  }

  // Get documents (from cloud if authenticated, otherwise local)
  static async getDocuments(): Promise<Document[] | LocalDocument[]> {
    const isAuth = await this.isAuthenticated();

    if (isAuth) {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      
      if (!user) return [];
      
      const { data, error } = await supabase
        .from("Documents")
        .select("*")
        .eq("user_id", user.id)  // CRITICAL: Filter by user_id
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching documents:", error);
        return [];
      }

      return data || [];
    }

    return this.getLocalDocuments();
  }

  // Save document (to cloud if authenticated, otherwise local)
  static async saveDocument(document: {
    title: string;
    originalText: string | null;
    editedText: string | null;
    duration?: number;
  }): Promise<Document | LocalDocument> {
    const isAuth = await this.isAuthenticated();

    if (isAuth) {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const { data, error } = await supabase
        .from("Documents")
        .insert({
          user_id: user!.id,
          title: document.title,
          original_text: document.originalText,
          edited_text: document.editedText,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    }

    return this.saveLocalDocument({
      ...document,
      duration: document.duration || 0,
    });
  }

  // Update document (cloud or local)
  static async updateDocument(
    id: string,
    updates: {
      title?: string;
      originalText?: string | null;
      editedText?: string | null;
      contentVersionIdx?: number | null;
    }
  ): Promise<Document | LocalDocument | null> {
    const isAuth = await this.isAuthenticated();

    if (isAuth) {
      const {
        data: { user },
      } = await supabase.auth.getUser();

      if (!user) return null;

      const updatePayload: Record<string, any> = {
        title: updates.title,
        original_text: updates.originalText,
        edited_text: updates.editedText,
        updated_at: new Date().toISOString(),
      };

      if (updates.contentVersionIdx !== undefined) {
        updatePayload.content_version_idx = updates.contentVersionIdx;
      }

      const { data, error } = await supabase
        .from("Documents")
        .update(updatePayload)
        .eq("id", id)
        .eq("user_id", user.id)  // CRITICAL: Ensure user owns the document
        .select()
        .single();

      if (error) {
        console.error("Error updating document:", error);
        return null;
      }
      return data;
    }

    return this.updateLocalDocument(id, updates);
  }

  // Delete document (cloud or local)
  static async deleteDocument(id: string): Promise<boolean> {
    const isAuth = await this.isAuthenticated();

    if (isAuth) {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      
      if (!user) return false;
      
      const { error } = await supabase
        .from("Documents")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);  // CRITICAL: Ensure user owns the document

      if (error) {
        console.error("Error deleting document:", error);
        return false;
      }

      return true;
    }

    return this.deleteLocalDocument(id);
  }
}
